import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../services/api';
import ReviewLinkSettings from '../components/ReviewLinkSettings';
import ReviewWidgetSettings from '../components/ReviewWidgetSettings';

const TONE_OPTIONS = [
  'Friendly',
  'Professional',
  'Warm',
  'Casual',
  'Formal',
  'Enthusiastic',
  'Empathetic',
  'Humorous',
];

const LANGUAGES = [
  { code: 'Spanish', label: '🇪🇸 Spanish' },
  { code: 'French', label: '🇫🇷 French' },
  { code: 'German', label: '🇩🇪 German' },
  { code: 'Italian', label: '🇮🇹 Italian' },
  { code: 'Portuguese', label: '🇵🇹 Portuguese' },
  { code: 'Dutch', label: '🇳🇱 Dutch' },
  { code: 'Polish', label: '🇵🇱 Polish' },
  { code: 'Arabic', label: '🇸🇦 Arabic' },
  { code: 'Hindi', label: '🇮🇳 Hindi' },
  { code: 'Mandarin Chinese', label: '🇨🇳 Chinese' },
  { code: 'Japanese', label: '🇯🇵 Japanese' },
  { code: 'Korean', label: '🇰🇷 Korean' },
  { code: 'Turkish', label: '🇹🇷 Turkish' },
  { code: 'Romanian', label: '🇷🇴 Romanian' },
  { code: 'Bengali', label: '🇧🇩 Bengali' },
  { code: 'Urdu', label: '🇵🇰 Urdu' },
];

export default function SettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [businessCategory, setBusinessCategory] = useState('local_business');
  const [settings, setSettings] = useState({
    business_type: '',
    description: '',
    tone_preferences: '',
    owner_first_name: '',
    humanize_responses: false,
    translate_enabled: false,
    translate_language: 'Spanish',
  });
  const [selectedTones, setSelectedTones] = useState([]);
  const [widgetRefreshKey, setWidgetRefreshKey] = useState(0);

  // Track whether translation was already enabled+saved in DB (language is locked)
  const [languageLocked, setLanguageLocked] = useState(false);

  // Account deletion state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const location = useLocation();

  // Deep-link scroll from onboarding card
  useEffect(() => {
    if (location.hash === '#review-link') {
      setTimeout(() => {
        const el = document.getElementById('review-link');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.style.outline = '2px solid var(--accent, #34d399)';
          el.style.outlineOffset = '4px';
          el.style.borderRadius = '12px';
          setTimeout(() => { el.style.outline = 'none'; }, 2000);
        }
      }, 300);
    }
  }, [location.hash]);

  useEffect(() => {
    // Fetch business category
    api.getBusinesses().then(businesses => {
      if (businesses.length > 0) {
        setBusinessCategory(businesses[0].business_category || 'local_business');
      }
    }).catch(() => {});

    api.getBusinessSettings()
      .then((data) => {
        setSettings({
          business_type: data.business_type || '',
          description: data.description || '',
          tone_preferences: data.tone_preferences || '',
          owner_first_name: data.owner_first_name || '',
          humanize_responses: data.humanize_responses || false,
          translate_enabled: data.translate_enabled || false,
          translate_language: data.translate_language || 'Spanish',
        });

        // Language is locked if translation was previously enabled
        if (data.translate_enabled && data.translate_language) {
          setLanguageLocked(true);
        }

        if (data.tone_preferences) {
          const existing = data.tone_preferences.split(',').map((t) => t.trim().toLowerCase());
          const matched = TONE_OPTIONS.filter((opt) =>
            existing.includes(opt.toLowerCase())
          );
          setSelectedTones(matched);
        }
      })
      .catch(() => setError('Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (e) => {
    setSaved(false);
    setSettings((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleToneToggle = (tone) => {
    setSaved(false);
    const updated = selectedTones.includes(tone)
      ? selectedTones.filter((t) => t !== tone)
      : [...selectedTones, tone];
    setSelectedTones(updated);
    setSettings((prev) => ({
      ...prev,
      tone_preferences: updated.map((t) => t.toLowerCase()).join(', '),
    }));
  };

  const handleHumanizeToggle = () => {
    setSaved(false);
    setSettings((prev) => ({ ...prev, humanize_responses: !prev.humanize_responses }));
  };

  const handleTranslateToggle = () => {
    setSaved(false);
    setSettings((prev) => ({ ...prev, translate_enabled: !prev.translate_enabled }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await api.updateBusinessSettings(settings);
      setSaved(true);
      // Once saved with translation enabled, lock the language
      if (settings.translate_enabled && settings.translate_language) {
        setLanguageLocked(true);
      }
    } catch (err) {
      setError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const data = await api.exportAccountData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `stellarreply-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to export data: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    setDeleteError('');
    setDeleting(true);
    try {
      await api.deleteAccount(deletePassword, deleteConfirmation);
      api.clearToken();
      window.location.href = '/';
    } catch (err) {
      setDeleteError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div>
        <div className="page-header">
          <h1 className="page-title">Settings</h1>
          <p className="page-subtitle">Loading...</p>
        </div>
      </div>
    );
  }

  const isHospitality = businessCategory === 'hospitality';
  const selectedLangLabel = LANGUAGES.find(l => l.code === settings.translate_language)?.label || settings.translate_language;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Settings</h1>
        <p className="page-subtitle">
          {isHospitality
            ? 'Update your property profile and AI response preferences'
            : 'Update your business profile and AI response preferences'}
        </p>
      </div>

      {saved && (
        <div className="card" style={{ padding: '16px', marginBottom: '20px', background: 'var(--green-light)', borderColor: 'var(--green)' }}>
          <strong style={{ color: 'var(--green)' }}>✓ Settings saved</strong>
          <p style={{ margin: '4px 0 0', fontSize: '14px' }}>Your changes will apply to all future AI-generated responses.</p>
        </div>
      )}

      {error && (
        <div className="card" style={{ padding: '16px', marginBottom: '20px', background: '#fef2f2', borderColor: '#ef4444' }}>
          <strong style={{ color: '#ef4444' }}>{error}</strong>
        </div>
      )}

      <div className="card" style={{ padding: '32px' }}>
        <form onSubmit={handleSubmit}>
          <div className="settings-field">
            <label htmlFor="business_type">
              {isHospitality ? 'Property type' : 'Business type'}
            </label>
            <input
              id="business_type"
              name="business_type"
              type="text"
              value={settings.business_type}
              onChange={handleChange}
              placeholder={isHospitality
                ? 'e.g. 2-bed flat, studio apartment, detached house'
                : 'e.g. Italian restaurant, dental practice, hair salon'}
            />
            <span className="settings-hint">
              {isHospitality
                ? 'Helps the AI understand your property when writing responses.'
                : 'Helps the AI understand your industry when writing responses.'}
            </span>
          </div>

          <div className="settings-field">
            <label htmlFor="description">
              {isHospitality ? 'Property description' : 'Business description'}
            </label>
            <textarea
              id="description"
              name="description"
              value={settings.description}
              onChange={handleChange}
              rows={3}
              placeholder={isHospitality
                ? 'e.g. Modern 2-bed flat in Bromley, 5 min walk from the station. Great for families and couples visiting London.'
                : 'e.g. Family-run Thai restaurant in Bromley, known for our pad thai and friendly atmosphere'}
            />
            <span className="settings-hint">
              {isHospitality
                ? 'The AI uses this to personalise responses and reference specific details about your property.'
                : 'The AI uses this to personalise responses and reference specific details about your business.'}
            </span>
          </div>

          <div className="settings-field">
            <label>Tone and style</label>
            <div className="tone-options">
              {TONE_OPTIONS.map((tone) => (
                <div
                  key={tone}
                  className={`tone-chip ${selectedTones.includes(tone) ? 'selected' : ''}`}
                  onClick={() => handleToneToggle(tone)}
                >
                  {tone}
                </div>
              ))}
            </div>
            <span className="settings-hint">Select one or more options. The AI will match this tone in generated responses.</span>
          </div>

          <div className="settings-field">
            <label htmlFor="owner_first_name">Sign-off name</label>
            <input
              id="owner_first_name"
              name="owner_first_name"
              type="text"
              value={settings.owner_first_name}
              onChange={handleChange}
              placeholder="e.g. Sarah"
            />
            <span className="settings-hint">
              {isHospitality
                ? 'Used to sign off AI responses, e.g. "Thanks for staying with us! — Sarah"'
                : 'Used to sign off AI responses. Leave blank to use your business name instead.'}
            </span>
          </div>

          <div className="settings-field">
            <label>Response style</label>
            <div
              className={`humanize-toggle ${settings.humanize_responses ? 'active' : ''}`}
              onClick={handleHumanizeToggle}
            >
              <div className="humanize-toggle-switch">
                <div className="humanize-toggle-knob" />
              </div>
              <div className="humanize-toggle-content">
                <div className="humanize-toggle-label">
                  {settings.humanize_responses ? '🧑 Extra-human mode ON' : '🤖 Standard mode'}
                </div>
                <div className="humanize-toggle-desc">
                  {settings.humanize_responses
                    ? 'Responses will use contractions, natural phrasing, and avoid corporate clichés. Sounds like a real person wrote it.'
                    : 'Clean, professional AI responses. Turn on extra-human mode to make responses sound more natural and less polished.'}
                </div>
              </div>
            </div>
          </div>

          <div className="settings-divider" />

          <div className="settings-field">
            <label>Review translation</label>
            <div
              className={`humanize-toggle ${settings.translate_enabled ? 'active' : ''}`}
              onClick={handleTranslateToggle}
            >
              <div className="humanize-toggle-switch">
                <div className="humanize-toggle-knob" />
              </div>
              <div className="humanize-toggle-content">
                <div className="humanize-toggle-label">
                  {settings.translate_enabled ? '🌐 Translation ON' : '🌐 Translation OFF'}
                </div>
                <div className="humanize-toggle-desc">
                  {settings.translate_enabled
                    ? isHospitality
                      ? 'Guest reviews will be translated so you can understand them in your language. Translations are saved automatically.'
                      : 'Customer reviews will be translated so you can understand them in your language. Translations are saved automatically.'
                    : isHospitality
                      ? 'Enable to translate guest reviews into your preferred language. Useful if you receive reviews from international guests.'
                      : 'Enable to translate customer reviews into your preferred language. Useful if you receive reviews in other languages.'}
                </div>
              </div>
            </div>

            {settings.translate_enabled && (
              <div style={{ marginTop: '12px' }}>
                <label className="settings-sub-label">Translate reviews to</label>
                {languageLocked ? (
                  <div className="translate-lang-locked">
                    <span className="translate-lang-locked-value">{selectedLangLabel}</span>
                    <span className="translate-lang-locked-hint">Language is locked once set and cannot be changed.</span>
                  </div>
                ) : (
                  <>
                    <select
                      className="translate-lang-picker"
                      name="translate_language"
                      value={settings.translate_language}
                      onChange={handleChange}
                    >
                      {LANGUAGES.map(lang => (
                        <option key={lang.code} value={lang.code}>{lang.label}</option>
                      ))}
                    </select>
                    <span className="settings-hint" style={{ color: '#ef4444', marginTop: '4px' }}>
                      ⚠️ Choose carefully. Once saved, the language cannot be changed.
                    </span>
                  </>
                )}
              </div>
            )}
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving}
            style={{ marginTop: '8px', padding: '12px 32px' }}
          >
            {saving ? 'Saving...' : 'Save changes'}
          </button>
        </form>
      </div>

      {/* Review Request Link / Feedback Funnel */}
      <div id="review-link" className="card" style={{ padding: '32px', marginTop: '24px' }}>
        <ReviewLinkSettings onSave={() => setWidgetRefreshKey(k => k + 1)} />
      </div>

      {/* Review Widget — only for local businesses */}
      {!isHospitality && (
        <div className="card" style={{ padding: '32px', marginTop: '24px' }}>
          <ReviewWidgetSettings key={widgetRefreshKey} />
        </div>
      )}

      {/* Data & Privacy */}
      <div className="card" style={{ padding: '32px', marginTop: '24px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '4px' }}>Data & Privacy</h2>
        <p style={{ fontSize: '14px', color: 'var(--text-light)', marginBottom: '20px' }}>
          Download your data or permanently delete your account.
        </p>

        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            className="btn btn-secondary"
            onClick={handleExportData}
            disabled={exporting}
            style={{ padding: '10px 20px' }}
          >
            {exporting ? 'Exporting...' : 'Download my data'}
          </button>
          <button
            className="btn"
            onClick={() => setShowDeleteModal(true)}
            style={{
              padding: '10px 20px',
              background: 'transparent',
              color: '#ef4444',
              border: '1px solid #ef4444',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '500',
            }}
          >
            Delete my account
          </button>
        </div>
      </div>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div className="card" style={{ padding: '32px', maxWidth: '480px', width: '90%' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#ef4444', marginBottom: '8px' }}>
              Delete your account
            </h2>
            <p style={{ fontSize: '14px', color: 'var(--text-light)', marginBottom: '20px' }}>
              This will permanently delete your account, all your business profiles,
              and all review data. This action cannot be undone. Your Stripe subscription
              will be cancelled automatically.
            </p>

            {deleteError && (
              <div style={{
                padding: '12px', marginBottom: '16px',
                background: '#fef2f2', border: '1px solid #ef4444',
                borderRadius: '8px', fontSize: '14px', color: '#ef4444',
              }}>
                {deleteError}
              </div>
            )}

            <form onSubmit={handleDeleteAccount}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
                  Enter your password
                </label>
                <input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  required
                  style={{ width: '100%' }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
                  Type <strong>DELETE MY ACCOUNT</strong> to confirm
                </label>
                <input
                  type="text"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  placeholder="DELETE MY ACCOUNT"
                  required
                  style={{ width: '100%' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeletePassword('');
                    setDeleteConfirmation('');
                    setDeleteError('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={deleting || deleteConfirmation !== 'DELETE MY ACCOUNT'}
                  style={{
                    padding: '10px 20px', borderRadius: '8px', fontWeight: '500',
                    border: 'none', cursor: 'pointer', fontSize: '14px',
                    background: deleteConfirmation === 'DELETE MY ACCOUNT' ? '#ef4444' : '#ccc',
                    color: 'white',
                  }}
                >
                  {deleting ? 'Deleting...' : 'Permanently delete account'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
